import type { VisitTemplate } from '../data/templates';

interface Props {
  template: VisitTemplate;
}

export function SummaryTab({ template }: Props) {
  return (
    <div className="summary-grid">
      {/* Column 1 */}
      <div className="summary-column">
        <section className="summary-card">
          <h3 className="summary-card-title">Diagnoses</h3>
          <div className="summary-card-body">
            {(template.diagnoses ?? template.icdCodes).map((dx) => (
              <div key={dx.code} className="summary-item">
                <span className="icd-code">{dx.code}</span>
              </div>
            ))}
          </div>
        </section>

        {template.socialHistory && (
          <section className="summary-card">
            <h3 className="summary-card-title">Social History</h3>
            <div className="summary-card-body">
              <table className="summary-table">
                <tbody>
                  {template.socialHistory.map((item) => (
                    <tr key={item.category}>
                      <td className="summary-table-label">{item.category}</td>
                      <td>{item.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

      </div>

      {/* Column 2 */}
      <div className="summary-column">
        {template.allergies && (
          <section className="summary-card">
            <h3 className="summary-card-title">Allergies</h3>
            <div className="summary-card-body">
              <table className="summary-table">
                <tbody>
                  <tr>
                    <td className="summary-table-label">Drug</td>
                    <td>{template.allergies.drug}</td>
                  </tr>
                  <tr>
                    <td className="summary-table-label">Food</td>
                    <td>{template.allergies.food}</td>
                  </tr>
                  <tr>
                    <td className="summary-table-label">Environmental</td>
                    <td>{template.allergies.environmental}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}

        {template.vitals && (
          <section className="summary-card">
            <h3 className="summary-card-title">Flowsheets / Vitals</h3>
            <div className="summary-card-body">
              <div className="vitals-table-wrapper">
                <table className="med-table vitals-table summary-vitals">
                  <thead>
                    <tr>
                      <th></th>
                      {template.vitals.map((row) => (
                        <th key={row.date} className={row.date === template.visitDate ? 'vitals-current-header' : ''}>
                          {row.date}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {template.vitals[0]?.vitals.map((v, vi) => (
                      <tr key={v.label}>
                        <td className="vitals-row-label">{v.label}</td>
                        {template.vitals!.map((row, ri) => {
                          const vital = row.vitals[vi];
                          return (
                            <td
                              key={row.date}
                              className={`${ri === 0 ? 'vitals-current-cell' : ''} ${vital?.flag === 'elevated' ? 'elevated-value' : ''}`}
                            >
                              {vital?.value ?? '—'}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {template.familyHistory && (
          <section className="summary-card">
            <h3 className="summary-card-title">Family Health History</h3>
            <div className="summary-card-body">
              <p className="summary-text">{template.familyHistory}</p>
            </div>
          </section>
        )}

        {template.pastMedicalHistory && (
          <section className="summary-card">
            <h3 className="summary-card-title">Past Medical History</h3>
            <div className="summary-card-body">
              <div className="summary-subsection">
                <strong>Major Events</strong>
                <ul className="summary-list">
                  {template.pastMedicalHistory.majorEvents.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
              <div className="summary-subsection">
                <strong>Preventive Care</strong>
                <ul className="summary-list">
                  {template.pastMedicalHistory.preventiveCare.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}

        {!template.vitals && (
          <section className="summary-card">
            <h3 className="summary-card-title">Assessment</h3>
            <div className="summary-card-body">
              {template.assessmentLines.length > 0 ? (
                <ol className="summary-list">
                  {template.assessmentLines.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ol>
              ) : (
                <p className="summary-text">No assessment lines</p>
              )}
            </div>
          </section>
        )}

        {!template.vitals && (
          <section className="summary-card">
            <h3 className="summary-card-title">Plan</h3>
            <div className="summary-card-body">
              {template.planItems.length > 0 ? (
                <ol className="summary-list">
                  {template.planItems.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ol>
              ) : (
                <p className="summary-text">No plan items</p>
              )}
            </div>
          </section>
        )}
      </div>

      {/* Column 3 */}
      <div className="summary-column">
        {template.encounters && (
          <section className="summary-card">
            <h3 className="summary-card-title">Encounters</h3>
            <div className="summary-card-body">
              {template.encounters.map((enc, i) => (
                <div key={i} className="summary-encounter">
                  <div className="summary-encounter-date">{enc.date}</div>
                  <div className="summary-encounter-type">{enc.type}</div>
                  <div className="summary-encounter-details">{enc.details}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="summary-card">
          <h3 className="summary-card-title">Quality of Care</h3>
          <div className="summary-card-body">
            {template.qualityFlags.map((flag, i) => (
              <div key={i} className="screening-item">
                <span className="screening-status screening-completed">{'\u2713'}</span>
                <span>{flag}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
